/**
 * Legacy show mode selector row (lower controls / middle section).
 * Prefer {@link PerformShowModeOverlay} on On Air / Next previews.
 */

import { useState } from 'react';
import { Box, CircularProgress, IconButton, SvgIcon, Tooltip } from '@mui/material';
import { PersonOutlined, PictureInPictureOutlined } from '@mui/icons-material';
import type { SvgIconComponent } from '@mui/icons-material';
import type { IconType } from 'react-icons';
import { GoFileMedia } from 'react-icons/go';
import { LiveContextShowMode } from '@/enums/live-context-show-mode.enum';
import { useUpdateLiveContextMutation } from '@/state/mutations/player/use-update-live-context';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { useTranslations } from '@/i18n/use-translations';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import type { LiveContextStateDto } from '@/state/queries/player/use-get-live-context-state';
import type { SxProps, Theme } from '@mui/material/styles';
import type { FlowControlPerformMode } from '@/types/player/simulation-cue-sheet.types';
import { useFlowControlSimulationCueStore } from '@/state/stores/use-flow-control-simulation-cue-store';
import { PerformEqualSpreadIconRow, performEqualSpreadIconButtonSx } from './perform-equal-spread-icon-row';

export interface PerformShowModeRowProps {
  flow: FlowDto;
  entityRef: string;
  performMode?: FlowControlPerformMode;
  liveContextState?: LiveContextStateDto;
  sx?: SxProps<Theme>;
  /** Lower controls: equal-width touch targets across the half-row on mobile. */
  equalSpreadOnMobile?: boolean;
}

const SHOW_MODE_OPTIONS: Array<{
  mode: LiveContextShowMode;
  titleKey:
    | 'flows.detail.perform.lowerControls.showMode.mediaOnly'
    | 'flows.detail.perform.lowerControls.showMode.mixedView'
    | 'flows.detail.perform.lowerControls.showMode.speakerOnly';
  Icon: SvgIconComponent | IconType;
}> = [
  {
    mode: LiveContextShowMode.MEDIA_ONLY,
    titleKey: 'flows.detail.perform.lowerControls.showMode.mediaOnly',
    Icon: GoFileMedia,
  },
  {
    mode: LiveContextShowMode.BOTH,
    titleKey: 'flows.detail.perform.lowerControls.showMode.mixedView',
    Icon: PictureInPictureOutlined,
  },
  {
    mode: LiveContextShowMode.SPEAKER_ONLY,
    titleKey: 'flows.detail.perform.lowerControls.showMode.speakerOnly',
    Icon: PersonOutlined,
  },
];

function ShowModeOptionIcon({ Icon }: { Icon: SvgIconComponent | IconType }) {
  if ('muiName' in Icon) {
    return <Icon fontSize="medium" />;
  }
  return <SvgIcon component={Icon} inheritViewBox fontSize="medium" />;
}

export function PerformShowModeRow({
  flow,
  entityRef,
  performMode = 'play',
  liveContextState,
  sx,
  equalSpreadOnMobile = false,
}: PerformShowModeRowProps) {
  const t = useTranslations();
  const setFlow = useFlowStore((s) => s.setFlow);
  const simulationOnAirShowMode = useFlowControlSimulationCueStore((s) => s.onAirShowMode);
  const simulationSetOnAirShowMode = useFlowControlSimulationCueStore((s) => s.setOnAirShowMode);
  const { mutateAsync: updateLiveContextMutation } = useUpdateLiveContextMutation();
  const [pendingMode, setPendingMode] = useState<LiveContextShowMode | null>(null);

  const showModeToLiveContextShowMode = (showMode: string): LiveContextShowMode =>
    showMode === 'both' || showMode === 'mixedView' ? LiveContextShowMode.BOTH : (showMode as LiveContextShowMode);

  const currentShowMode =
    performMode === 'simulation'
      ? simulationOnAirShowMode === 'both'
        ? LiveContextShowMode.BOTH
        : simulationOnAirShowMode === 'speakerOnly'
          ? LiveContextShowMode.SPEAKER_ONLY
          : LiveContextShowMode.MEDIA_ONLY
      : liveContextState?.playState?.showMode != null
        ? showModeToLiveContextShowMode(liveContextState.playState.showMode)
        : LiveContextShowMode.MEDIA_ONLY;

  const isPending = pendingMode != null;

  const handleModeChange = async (newMode: LiveContextShowMode) => {
    if (newMode === currentShowMode || isPending) {
      return;
    }
    if (performMode === 'simulation') {
      const mapped =
        newMode === LiveContextShowMode.BOTH
          ? 'both'
          : newMode === LiveContextShowMode.SPEAKER_ONLY
            ? 'speakerOnly'
            : 'mediaOnly';
      simulationSetOnAirShowMode(mapped);
      return;
    }
    if (!flow?.liveContext || !entityRef) return;
    const separator = entityRef.indexOf(':');
    if (separator < 0) return;
    const entityType = entityRef.slice(0, separator) as 'Flow' | 'Event';
    const entityId = entityRef.slice(separator + 1);
    if (!entityId) return;

    const original = flow.liveContext.showMode;
    setPendingMode(newMode);
    setFlow({
      ...flow,
      liveContext: { ...flow.liveContext, showMode: newMode },
    });
    try {
      await updateLiveContextMutation({
        entityType,
        entityId,
        showMode: newMode,
      });
    } catch {
      setFlow({
        ...flow,
        liveContext: { ...flow.liveContext, showMode: original },
      });
    } finally {
      setPendingMode(null);
    }
  };

  const activeColor = performMode === 'simulation' ? 'info' : 'error';

  const buttons = SHOW_MODE_OPTIONS.map(({ mode, titleKey, Icon }) => {
    const isActive = currentShowMode === mode;
    const isTargetPending = pendingMode === mode;
    return (
      <Tooltip key={mode} title={t(titleKey)}>
        <span style={equalSpreadOnMobile ? { display: 'flex', width: '100%', justifyContent: 'center' } : undefined}>
          <IconButton
            onClick={() => void handleModeChange(mode)}
            color={isActive || isTargetPending ? activeColor : 'success'}
            size="medium"
            disabled={isPending}
            aria-busy={isTargetPending}
            sx={equalSpreadOnMobile ? performEqualSpreadIconButtonSx : undefined}
          >
            {isTargetPending ? <CircularProgress size={22} color="inherit" /> : <ShowModeOptionIcon Icon={Icon} />}
          </IconButton>
        </span>
      </Tooltip>
    );
  });

  if (equalSpreadOnMobile) {
    return <PerformEqualSpreadIconRow sx={sx}>{buttons}</PerformEqualSpreadIconRow>;
  }

  return <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2, ...sx }}>{buttons}</Box>;
}
